import React, { useState, useRef, useEffect, useCallback, useMemo } from "react"
import { Button, Layout, Spin, Typography, Row, Col, Empty, Modal, message } from "antd"
import { SearchOutlined, EnvironmentOutlined, TrophyOutlined, ProfileOutlined, SafetyCertificateOutlined, SettingOutlined, FilterOutlined, ExternalLinkOutlined } from '@ant-design/icons';
import { Formik, Form } from "formik"
import { useHistory, useLocation, matchPath } from "react-router-dom"

import FlexibleSearchForm, { validationSchema } from "./JobBoardSearchForm"
import { MenuUnfoldOutlined, MenuFoldOutlined, FileAddFilled } from '@ant-design/icons';
import * as JobBoardFormFields from "./JobBoardFormFields"

import { useJobBoardSearch, useSearchParams, useSearchResults } from "../context/searchContext"
import cbAdapter from "../adapters/CBAdapter"
import { useSearchHistory } from "../hooks/useSearchHistory"

import { useSearchFunction } from "../utils/useSearchFuntion"
import { CB_SEARCH, CB_SEARCH_V2 } from "../../../../../ApiUrl"
import SearchLanding from "./CBSearchPage"
import CBSearchResults from "./CBSearchResults"
import cssStyles from './CB.module.css'
import ResizableSidebar from "../components/ResizableSidebar"
import SearchFilters from './SearchFilters'
import CandidateDetailView from "./CandidateDetailView"
import CompareView from "./CompareView"
import {
  getJobBoardBasePath,
  constructSearchPath,
  constructCandidatePath,
  constructComparePath,
  parseSearchParams,
  parseCompareParams
} from '../utils/routeUtils';
import { PanelGroup, Panel, PanelResizeHandle } from 'react-resizable-panels';
import DetailsSkeleton from '../components/DetailsSkeleton';
import { NotesProvider } from '../context/notesContext';

const { Title, Text } = Typography
const { Header, Content, Sider } = Layout

export const initialFields = [
  { name: "keyword", label: "Keyword", type: "KeywordField", props: { allowClear: true, autoFocus: true } },
  { name: "jobTitle", label: "Job Title", type: "JobTitleField" },
  { name: "location", label: "Location", type: "LocationField" },
  {
    name: "postalCode",
    label: "ZIP Code",
    type: "PostalCodeField",
    props: {
      placeholder: "Enter ZIP code"
    }
  },
  {
    name: "radius",
    label: "Radius",
    type: "SelectField",
    props: {
      options: [
        { value: "10", label: "10 miles" },
        { value: "20", label: "20 miles" },
        { value: "30", label: "30 miles" },
        { value: "50", label: "50 miles" },
      ],
      defaultValue: "30"
    },
  },
  {
    name: "updatedWithin",
    label: "Resume Freshness",
    type: "FreshnessInDaysField",
    props: {
      placeholder: "Filter by resume update date"
    }
  },
]

export const advancedFields = [
  { name: "targetJobTitle", label: "Target Job Title", type: "JobTitleField" },
  { name: "recentCompany", label: "Recent Company", type: "TextField" },
  { name: "schoolName", label: "School Name", type: "TextField" },
  { name: "recentJobDescription", label: "Recent Job Description", type: "TextField" },
  {
    name: "keywordBehavior",
    label: "Keyword Search Type",
    type: "KeywordBehaviorField",
  },
  {
    name: "targetWorkLocation",
    label: "Target Work Location",
    type: "LocationField",
  },
  {
    name: "targetWorkLocationBoolean",
    label: "Target Location Logic",
    type: "SelectField",
    props: {
      options: [
        { value: "0", label: "Match Either Location (OR)" },
        { value: "1", label: "Match Both Locations (AND)" },
      ],
    },
  },
  {
    name: "targetWorkSite",
    label: "Target Work Site",
    type: "SelectField",
    props: {
      options: [
        { value: "1", label: "On-site" },
        { value: "2", label: "Off-site" },
        { value: "3", label: "No Preference" },
      ],
    },
  },
  {
    name: "experience",
    label: "Years of Experience",
    type: "SelectField",
    props: {
      mode: "multiple",
      options: [
        { value: "1", label: "Less than 1 year" },
        { value: "2", label: "1-2 years" },
        { value: "3", label: "2-3 years" },
        { value: "4", label: "3-5 years" },
        { value: "5", label: "5-7 years" },
        { value: "6", label: "7-10 years" },
        { value: "7", label: "More than 10 years" },
      ],
    },
  },
  {
    name: "CBMinimumExperience",
    label: "Minimum Experience (Years)",
    type: "NumberField",
    props: {
      min: 0,
      max: 50
    }
  },
  {
    name: "CBMaximumExperience",
    label: "Maximum Experience (Years)",
    type: "NumberField",
    props: {
      min: 0,
      max: 50
    }
  },
  {
    name: "education",
    label: "Education Level",
    type: "SelectField",
    props: {
      mode: "multiple",
      options: [
        { value: "1", label: "High School" },
        { value: "2", label: "Associate's Degree" },
        { value: "3", label: "Bachelor's Degree" },
        { value: "4", label: "Master's Degree" },
        { value: "5", label: "Doctorate" },
      ],
    },
  },
  {
    name: "educationMajor",
    label: "Field of Study",
    type: "TextField",
  },
  {
    name: "careerLevel",
    label: "Career Level",
    type: "SelectField",
    props: {
      mode: "multiple",
      options: [
        { value: "1", label: "Student/Intern" },
        { value: "2", label: "Entry Level" },
        { value: "3", label: "Experienced" },
        { value: "4", label: "Manager" },
        { value: "5", label: "Executive" },
      ],
    },
  },
  {
    name: "workAuthorization",
    label: "Work Authorization",
    type: "SelectField",
    props: {
      mode: "multiple",
      options: [
        { value: "1", label: "US Citizen" },
        { value: "2", label: "Permanent Resident" },
        { value: "3", label: "H1B Visa" },
        { value: "4", label: "TN Permit" },
        { value: "5", label: "Employment Authorization Document" },
      ],
    },
  },
  {
    name: "militaryExperience",
    label: "Military Experience",
    type: "SelectField",
    props: {
      mode: "multiple",
      options: [
        { value: "1", label: "Active Military" },
        { value: "2", label: "Military Spouse/Partner" },
        { value: "3", label: "Veteran" },
        { value: "4", label: "No Military Experience" },
      ],
    },
  },
  {
    name: "nicheInclusion",
    label: "Include Niche Networks",
    type: "NicheInclusionField"
  },
  {
    name: "lemmatize",
    label: "Use Word Variations",
    type: "CheckboxField",
    props: {
      label: "Include different forms of keywords"
    }
  },
  {
    name: "removeDuplicate",
    label: "Remove Duplicates",
    type: "CheckboxField",
    props: {
      label: "Filter out duplicate candidates"
    }
  },
  {
    name: "searchRadiusInKilometers",
    label: "Use Kilometers for Radius",
    type: "CheckboxField",
    props: {
      label: "Use kilometers instead of miles"
    }
  },
  {
    name: "applyLastActivity",
    label: "Apply Last Activity Filter",
    type: "CheckboxField",
    props: {
      label: "Filter by candidate's last activity"
    }
  },
  {
    name: "searchWithMostRecentJobTitle",
    label: "Use Most Recent Job Title",
    type: "CheckboxField",
    props: {
      label: "Prioritize most recent job title"
    }
  },
  {
    name: "searchWithMostRecentCompany",
    label: "Use Most Recent Company",
    type: "CheckboxField",
    props: {
      label: "Prioritize most recent company"
    }
  },
  { name: "willingToRelocate", label: "Willing to Relocate", type: "CheckboxField" },
  {
    name: "workStatus",
    label: "Work Status",
    type: "SelectField",
    props: {
      options: [
        { value: "CTAY", label: "Can Start Immediately" },
        { value: "CT2W", label: "Available in 2 Weeks" },
        { value: "CT1M", label: "Available in 1 Month" },
        { value: "CTPD", label: "Passive Job Seeker" }
      ],
    },
  },
  {
    name: "securityClearance",
    label: "Security Clearance",
    type: "MultiSelectField",
    props: {
      options: [
        { value: "12", label: "Active Confidential" },
        { value: "13", label: "Active Secret" },
        { value: "14", label: "Active Top Secret" },
        { value: "15", label: "Inactive Confidential" },
        { value: "16", label: "Inactive Secret" },
        { value: "17", label: "Inactive Top Secret" },
      ],
    },
  },
  { name: "managerialExperience", label: "Has Managerial Experience", type: "CheckboxField" },
  { name: "projectLeadership", label: "Has Project Leadership Experience", type: "CheckboxField" },
  {
    name: "minimumEmployeesManaged",
    label: "Minimum Employees Managed",
    type: "MinimumEmployeesManagedField"
  },
  {
    name: "maximumCommute",
    label: "Maximum Commute Time",
    type: "MaximumCommuteField"
  },
  {
    name: "firstAlternateLocation",
    label: "First Alternate Location",
    type: "FirstAlternateLocationField"
  },
  {
    name: "secondAlternateLocation",
    label: "Second Alternate Location",
    type: "SecondAlternateLocationField"
  },
  {
    name: "minimumDegree",
    label: "Minimum Degree",
    type: "MinimumDegreeField"
  },
  {
    name: "employmentType",
    label: "Employment Type",
    type: "EmploymentTypeField"
  },
  {
    name: "minimumTravelRequirement",
    label: "Minimum Travel Requirement",
    type: "MinimumTravelRequirementField"
  },
  {
    name: "currentlyEmployed",
    label: "Currently Employed",
    type: "CurrentlyEmployedField"
  },
  {
    name: "compensationType",
    label: "Compensation Type",
    type: "CompensationTypeField"
  },
  {
    name: "minimumSalary",
    label: "Minimum Salary",
    type: "MinimumSalaryField"
  },
  {
    name: "maximumSalary",
    label: "Maximum Salary",
    type: "MaximumSalaryField"
  },
  {
    name: "excludeResumesWithNoSalary",
    label: "Exclude Resumes With No Salary",
    type: "ExcludeResumesWithNoSalaryField"
  },
  {
    name: "languagesSpoken",
    label: "Languages Spoken",
    type: "LanguagesSpokenField"
  },
  {
    name: "excludeIVRResumes",
    label: "Exclude IVR Resumes",
    type: "ExcludeIVRResumesField"
  },
  {
    name: "sortBy",
    label: "Sort Results By",
    type: "SelectField",
    props: {
      options: [
        { value: "Relevance", label: "Most Relevant" },
        { value: "Date", label: "Most Recent" },
        { value: "Distance", label: "Distance" },
      ],
    },
  },
]

const useStyles = {
  pageLayout: {
    height: '100vh',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  contentLayout: {
    flex: 1,
    display: 'flex',
    overflow: 'hidden',
  },
  sidebarLayout: {
    backgroundColor: '#fff',
    borderRight: '1px solid #f0f0f0',
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    overflow: 'hidden',
  },
  sidebarHeader: {
    padding: '16px',
    borderBottom: '1px solid #f0f0f0',
    background: '#fff',
    zIndex: 1,
  },
  sidebarContent: {
    flex: 1,
    overflow: 'auto',
    padding: '16px',
  },
  mainContent: {
    flex: 1,
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
  },
  mainHeader: {
    padding: '16px',
    background: '#fff',
    borderBottom: '1px solid #f0f0f0',
    zIndex: 1,
  },
  mainResults: {
    flex: 1,
    overflow: 'auto',
    padding: '16px',
  }
}

const modalStyles = {
  content: {
    padding: '0',
    borderRadius: '16px',
    maxWidth: '1200px',
    margin: '0 auto',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    height: '90vh',
    backgroundColor: '#f5f6fa',
  },
  header: {
    padding: '24px 32px',
    background: 'linear-gradient(135deg, #2e7d32 0%, #4caf50 100%)',
    color: 'white',
    marginBottom: 0,
    position: 'relative',
    zIndex: 1,
    borderTopLeftRadius: '16px',
    borderTopRightRadius: '16px',
  },
  title: {
    color: 'white',
    fontSize: '28px',
    fontWeight: 600,
    marginBottom: '8px',
  },
  description: {
    color: 'rgba(255, 255, 255, 0.85)',
    fontSize: '16px',
  },
  formContainer: {
    flex: 1,
    overflow: 'auto',
    padding: '24px 32px',
    backgroundColor: '#f5f6fa',
  },
  formSection: {
    background: 'white',
    borderRadius: '12px',
    padding: '24px',
    marginBottom: '24px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
  },
  sectionTitle: {
    fontSize: '18px',
    fontWeight: 500,
    color: '#2e7d32',
    marginBottom: '20px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  fieldsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
    gap: '16px',
    alignItems: 'start',
  },
  footer: {
    padding: '16px 32px',
    background: 'white',
    borderTop: '1px solid #f0f0f0',
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '12px',
    position: 'sticky',
    bottom: 0,
    zIndex: 1,
    borderBottomLeftRadius: '16px',
    borderBottomRightRadius: '16px',
  },
};

const formSections = [
  {
    title: "Basic Search",
    icon: <SearchOutlined />,
    fields: ["keyword", "keywordBehavior", "targetJobTitle", "recentCompany", "recentJobDescription", "schoolName"]
  },
  {
    title: "Location & Work Site",
    icon: <EnvironmentOutlined />,
    fields: [
      "location", 
      "postalCode", 
      "radius", 
      "updatedWithin",
      "searchRadiusInKilometers", 
      "targetWorkLocation", 
      "targetWorkLocationBoolean", 
      "targetWorkSite", 
      "willingToRelocate",
      "maximumCommute",
      "firstAlternateLocation",
      "secondAlternateLocation"
    ]
  },
  {
    title: "Experience & Education",
    icon: <TrophyOutlined />,
    fields: [
      "experience", 
      "CBMinimumExperience", 
      "CBMaximumExperience", 
      "education", 
      "educationMajor", 
      "minimumDegree", 
      "careerLevel", 
      "managerialExperience", 
      "minimumEmployeesManaged", 
      "projectLeadership"
    ]
  },
  {
    title: "Employment Preferences",
    icon: <ProfileOutlined />,
    fields: [
      "employmentType", 
      "minimumTravelRequirement", 
      "currentlyEmployed", 
      "compensationType", 
      "minimumSalary", 
      "maximumSalary", 
      "excludeResumesWithNoSalary", 
      "languagesSpoken"
    ]
  },
  {
    title: "Additional Qualifications",
    icon: <SafetyCertificateOutlined />,
    fields: [
      "workAuthorization", 
      "securityClearance", 
      "militaryExperience"
    ]
  },
  {
    title: "Search Options",
    icon: <SettingOutlined />,
    fields: [
      "sortBy", 
      "removeDuplicate", 
      "lemmatize", 
      "nicheInclusion", 
      "applyLastActivity", 
      "searchWithMostRecentJobTitle", 
      "searchWithMostRecentCompany", 
      "excludeIVRResumes"
    ]
  }
];

const modalVariants = {
  hidden: { opacity: 0, scale: 0.9 },
  visible: {
    opacity: 1,
    scale: 1,
    transition: {
      type: "spring",
      stiffness: 300,
      damping: 30
    }
  },
  exit: {
    opacity: 0,
    scale: 0.9,
    transition: {
      duration: 0.2
    }
  }
};

const CBSearch = React.memo(() => {
  const initialFieldsData = useMemo(() => initialFields, []);
  const formSectionsData = useMemo(() => formSections.map(section => ({
    ...section,
    icon: React.cloneElement(section.icon, {
      style: {
        color: '#2e7d32',
        fontSize: '18px',
        marginRight: '8px'
      }
    })
  })), []);
  
  const { searchParams, loading: searchLoading, error: searchError } = useSearchParams();
  const { results, totalResults } = useSearchResults();
  const { dispatch } = useJobBoardSearch();
  const history = useHistory();
  const location = useLocation();

  const [localState, setLocalState] = useState({
    showAdvancedFilters: false,
    sidebarCollapsed: false,
    searchType: "boolean",
    searchSuggestions: [],
    isAdvanceOptionsOpen: false,
    currentSearchValues: {}
  });

  const updateLocalState = useCallback((updates) => {
    if (typeof updates === 'function') {
      setLocalState(prev => ({...prev, ...updates(prev)}));
    } else {
      setLocalState(prev => ({...prev, ...updates}));
    }
  }, []);

  const { search } = useSearchFunction(CB_SEARCH_V2);
  const { addRecentSearch } = useSearchHistory();
  
  const lastRequestRef = useRef(null);
  const lastPaginationRef = useRef({ timestamp: 0, params: null });

  const getBasePath = useCallback(() => {
    return getJobBoardBasePath(location.pathname, 'cbV2');
  }, [location.pathname]);


  const submitSearch = useCallback(async (values, page = 1, pageSize = 20) => {
    const requestKey = JSON.stringify({ ...values, page, pageSize });
    
    if (lastRequestRef.current === requestKey) {
      return;
    }
    
    lastRequestRef.current = requestKey;
    
    try {
      
      values = values || {};
      
      if (typeof values === 'string') {
        values = { keyword: values };
      }
      
      let hasSearchCriteria = !!(values.keyword || values.jobTitle || values.targetJobTitle);
      
      if (!hasSearchCriteria && localState.currentSearchValues) {
        const { keyword, jobTitle, targetJobTitle } = localState.currentSearchValues;
        hasSearchCriteria = !!(keyword || jobTitle || targetJobTitle);
        
        if (hasSearchCriteria) {
          if (keyword && !values.keyword) values.keyword = keyword;
          if (jobTitle && !values.jobTitle) values.jobTitle = jobTitle;
          if (targetJobTitle && !values.targetJobTitle) values.targetJobTitle = targetJobTitle;
        }
      }
      
      if (!hasSearchCriteria && searchParams) {
        const { keyword, jobTitle, targetJobTitle } = searchParams;
        hasSearchCriteria = !!(keyword || jobTitle || targetJobTitle);
        
        if (hasSearchCriteria) {
          if (keyword && !values.keyword) values.keyword = keyword;
          if (jobTitle && !values.jobTitle) values.jobTitle = jobTitle;
          if (targetJobTitle && !values.targetJobTitle) values.targetJobTitle = targetJobTitle;
        }
      }
      
      if (!hasSearchCriteria && initialFormValues) {
        const { keyword, jobTitle, targetJobTitle } = initialFormValues;
        hasSearchCriteria = !!(keyword || jobTitle || targetJobTitle);
        
        if (hasSearchCriteria) {
          if (keyword && !values.keyword) values.keyword = keyword;
          if (jobTitle && !values.jobTitle) values.jobTitle = jobTitle;
          if (targetJobTitle && !values.targetJobTitle) values.targetJobTitle = targetJobTitle;
        }
      }
      
      if (!hasSearchCriteria) {
        console.error('No search criteria found in any source:', {
          values,
          currentSearchValues: localState.currentSearchValues,
          searchParams,
          initialFormValues
        });
        message.error('Please enter at least a keyword or job title to search');
        return;
      }
      
      if (values.keyword === undefined || values.keyword === null) {
        values.keyword = '';
      }
      
      if (!values.radius) {
        values.radius = "30";
      }
  
      const processedValues = {};
      
      Object.entries(values).forEach(([key, value]) => {
        if (value === 'true') {
          processedValues[key] = true;
        } else if (value === 'false') {
          processedValues[key] = false;
        } else if (value === undefined || value === null || value === '') {
        } else {
          processedValues[key] = value;
        }
      });

      
      updateLocalState(prev => ({
        ...prev,
        currentSearchValues: {
          ...prev.currentSearchValues,
          ...processedValues
        }
      }));
      
      dispatch({ type: "SET_LOADING", payload: true });
      dispatch({ type: "SET_ERROR", payload: null });

      const mergedValues = {
        ...searchParams,
        ...processedValues,
        page: Number(page),
        pageSize: Number(pageSize),
        radius: processedValues.radius || "30" 
      };


      const basePath = getBasePath();
      const searchPath = constructSearchPath(basePath, mergedValues);
      history.push(searchPath);

      setTimeout(async () => {
        if (lastRequestRef.current !== requestKey) {
          return;
        }
        
        dispatch({ type: "SET_LOADING", payload: true });
        
        try {
          const searchResults = await search(mergedValues, cbAdapter);
          
          
          if (!searchResults) {
            throw new Error('Search was interrupted. Please try again.');
          }
          
         
          if (searchResults.error) {
            throw new Error(searchResults.error);
          }

          if (!searchResults.results || searchResults.results.length === 0) {
            message.info('No candidates found matching your search criteria');
          }

          dispatch({
            type: "SET_RESULTS",
            payload: { 
              results: searchResults?.results || [],
              total: searchResults?.total || 0
            },
          });
          dispatch({ type: "SET_SEARCH_PARAMS", payload: mergedValues });

          if (searchResults?.total !== undefined && values.keyword) {
            const searchData = {
              title: values.keyword,
              query: mergedValues,
              matches: searchResults.total
            };

            try {
              await addRecentSearch(searchData);
              updateLocalState(prev => ({
                searchSuggestions: [searchData, ...prev.searchSuggestions].slice(0, 5)
              }));
            } catch (error) {
              console.error('[CBSearch] Failed to save search history:', error);
              message.error('Failed to save search history');
            }
          }
        } catch (error) {
          console.error('[CBSearch] Search error:', error);
          dispatch({ type: "SET_ERROR", payload: error.message });
          dispatch({
            type: "SET_RESULTS",
            payload: { 
              results: [],
              total: 0
            },
          });
          message.error('Search failed: ' + error.message);
        } finally {
          dispatch({ type: "SET_LOADING", payload: false });
        }
      }, 50); 
      
    } catch (error) {
      console.error('[CBSearch] Error preparing search:', error);
      message.error('Error preparing search: ' + error.message);
      dispatch({ type: "SET_LOADING", payload: false });
    }
  }, [searchParams, search, dispatch, addRecentSearch, updateLocalState, history, getBasePath]);

  const handlePageChange = useCallback((page, pageSize) => {
    const callParams = `${page}-${pageSize}`;
    const now = Date.now();
    
    const { timestamp: lastTime, params: lastParams } = lastPaginationRef.current;
    
    if (lastParams === callParams && now - lastTime < 100) {
      return;
    }
    
    lastPaginationRef.current = {
      timestamp: now,
      params: callParams
    };
    
    submitSearch(searchParams, page, pageSize);
  }, [searchParams, submitSearch]);

  const handleSubmit = useCallback((values, page = 1, pageSize = 20) => {
    return submitSearch(values, page, pageSize);
  }, [submitSearch]);

  const handleCandidateSelect = useCallback((candidate) => {
    const basePath = getBasePath();
    const candidatePath = constructCandidatePath(basePath, candidate.id);
    history.push(candidatePath);
  }, [history, getBasePath]);

  const handleCompareView = useCallback((candidates) => {
    const basePath = getBasePath();
    const comparePath = constructComparePath(basePath, candidates.map(c => c.id));
    history.push(comparePath);
  }, [history, getBasePath]);

  const handleExternalView = useCallback((candidate) => {
    const basePath = getBasePath();
    const candidatePath = constructCandidatePath(basePath, candidate.id);
    window.open(candidatePath, '_blank');
  }, [getBasePath]);

  const initialFormValues = useMemo(() => {
    if (location.search) {
      return parseSearchParams(location.search);
    }
    return searchParams || {};
  }, [location.search, searchParams]);

  const { pathname } = location;
  const isResultsView = pathname.includes('/results');
  const isCompareView = pathname.includes('/compare');
  const candidateId = pathname.includes('/candidate/') ? pathname.split('/').pop() : null;

  const renderContent = () => {
    if (isCompareView) {
      const candidateIds = parseCompareParams(location.search);
      const selectedCandidates = results.filter(c => candidateIds.includes(c.id));
      
      return (
        <Layout style={useStyles.contentLayout}>
          <CompareView
            candidates={selectedCandidates}
            onClose={() => {
              const basePath = getBasePath();
              history.push(`${basePath}/results${location.search}`);
            }}
            onViewCandidate={(candidate) => {
              const basePath = getBasePath();
              const candidatePath = constructCandidatePath(basePath, candidate.id);
              history.push(candidatePath);
            }}
            onRemoveFromCompare={(candidateId) => {
              const remainingIds = selectedCandidates
                .filter(c => c.id !== candidateId)
                .map(c => c.id);
              
              if (remainingIds.length < 2) {
                const basePath = getBasePath();
                history.push(`${basePath}/results${location.search}`);
              } else {
                const basePath = getBasePath();
                const comparePath = constructComparePath(basePath, remainingIds);
                history.push(comparePath);
              }
            }}
          />
        </Layout>
      );
    }

    if (isResultsView) {
      return (
        <Layout style={useStyles.contentLayout}>
          <ResizableSidebar
            collapsed={localState.sidebarCollapsed}
            onCollapse={() => updateLocalState({ sidebarCollapsed: !localState.sidebarCollapsed })}
            width={300}
            collapsedWidth={80}
          >
            <SearchFilters
              searchParams={searchParams}
              onSubmit={handleSubmit}
              isInSidebar={true}
            />
          </ResizableSidebar>
          <PanelGroup direction="horizontal">
            <Panel defaultSize={55} minSize={40} maxSize={65}>
              <CBSearchResults
                results={results}
                loading={searchLoading}
                error={searchError}
                searchQuery={searchParams}
                totalResults={totalResults}
                onPageChange={handlePageChange} 
                onCandidateSelect={handleCandidateSelect}
                onCompareView={handleCompareView}
                onExternalView={handleExternalView}
                selectedCandidateId={candidateId}
                onBackToLanding={() => {
                  history.push(getBasePath());
                }}
                onSortChange={(updatedParams) => {
                  handleSubmit(updatedParams, 1);
                }}
              />
            </Panel>

            <PanelResizeHandle>
              <div style={{
                width: '4px',
                backgroundColor: '#f0f0f0',
                cursor: 'col-resize',
                transition: 'background-color 0.3s',
                '&:hover': {
                  backgroundColor: '#d9d9d9'
                }
              }} />
            </PanelResizeHandle>

            <Panel defaultSize={45} minSize={35} maxSize={60}>
              <Layout style={{ height: '100%', backgroundColor: '#fff' }}>
                {searchLoading ? (
                  <DetailsSkeleton />
                ) : candidateId ? (
                  <CandidateDetailView
                    candidate={results.find(c => c.id === candidateId)}
                    onNext={() => {
                      const currentIndex = results.findIndex((c) => c.id === candidateId);
                      if (currentIndex < results.length - 1) {
                        handleCandidateSelect(results[currentIndex + 1]);
                      }
                    }}
                    onPrevious={() => {
                      const currentIndex = results.findIndex((c) => c.id === candidateId);
                      if (currentIndex > 0) {
                        handleCandidateSelect(results[currentIndex - 1]);
                      }
                    }}
                    hasNext={results.findIndex((c) => c.id === candidateId) < results.length - 1}
                    hasPrevious={results.findIndex((c) => c.id === candidateId) > 0}
                  />
                ) : (
                  <Empty
                    description="Select a candidate to view details"
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                  />
                )}
              </Layout>
            </Panel>
          </PanelGroup>
        </Layout>
      );
    }

    return (
      <SearchLanding
        initialFields={initialFieldsData}
        onSubmit={(values) => {
   
          updateLocalState({ currentSearchValues: values });
          handleSubmit(values);
        }}
        searchParams={initialFormValues}
        handleOpenAdvanceOptions={(formValues) => {
          const valuesToUse = formValues || 
            (Object.keys(localState.currentSearchValues).length > 0 
              ? localState.currentSearchValues 
              : initialFormValues);
          
          dispatch({ type: "SET_SEARCH_PARAMS", payload: valuesToUse });
          updateLocalState({ 
            currentSearchValues: valuesToUse,
            isAdvanceOptionsOpen: true 
          });
        }}
        searchSuggestions={localState.searchSuggestions}
        onFieldChange={(fieldName, value) => {
        }}
      />
    );
  };

  const modalContent = useMemo(() => (
    <div style={{
      ...modalStyles.content,
      backgroundColor: '#2e7d32',
      borderRadius: '16px',
      overflow: 'hidden'
    }} className={cssStyles.cbContainer}>
      <div style={{
        ...modalStyles.header,
        borderTopLeftRadius: '16px',
        borderTopRightRadius: '16px',
        padding: '24px 32px',
        marginTop: 0
      }}>
        <Title level={3} style={modalStyles.title}>Advanced Search Options</Title>
        <Text style={modalStyles.description}>
          Refine your search with additional filters to find the most relevant candidates
        </Text>
      </div>

      <div style={{
        ...modalStyles.formContainer,
        backgroundColor: '#f5f6fa'
      }}>
        <Formik
          initialValues={(() => {
            const values = {};
            
            if (searchParams) {
              Object.assign(values, searchParams);
            }
            
            if (localState.currentSearchValues) {
              Object.assign(values, localState.currentSearchValues);
            }
            
            const keyword = values.keyword || searchParams?.keyword || localState.currentSearchValues?.keyword || '';
            const jobTitle = values.jobTitle || searchParams?.jobTitle || localState.currentSearchValues?.jobTitle || '';
            const targetJobTitle = values.targetJobTitle || searchParams?.targetJobTitle || localState.currentSearchValues?.targetJobTitle || '';
            
            values.keyword = keyword;
            values.jobTitle = jobTitle;
            values.targetJobTitle = targetJobTitle;
            
            
            return values;
          })()}
          validationSchema={validationSchema}
          onSubmit={(values) => {
            const mergedValues = { ...values };
            
            if (!mergedValues.keyword && localState.currentSearchValues.keyword) {
              mergedValues.keyword = localState.currentSearchValues.keyword;
            }
            
            if (!mergedValues.jobTitle && localState.currentSearchValues.jobTitle) {
              mergedValues.jobTitle = localState.currentSearchValues.jobTitle;
            }
            
            if (!mergedValues.targetJobTitle && localState.currentSearchValues.targetJobTitle) {
              mergedValues.targetJobTitle = localState.currentSearchValues.targetJobTitle;
            }
            
            handleSubmit(mergedValues);
            updateLocalState({ isAdvanceOptionsOpen: false });
          }}
          enableReinitialize={true}
        >
          {({ handleSubmit, values, setFieldValue }) => {
            
            return (
              <Form layout="vertical">
                <div style={{ marginBottom: '16px', padding: '8px', backgroundColor: '#f0f8ff', borderRadius: '4px' }}>
                  <Text strong>Current Search Criteria:</Text>
                  {values.keyword && (
                    <div><Text type="secondary">Keyword:</Text> {values.keyword}</div>
                  )}
                  {values.jobTitle && (
                    <div><Text type="secondary">Job Title:</Text> {values.jobTitle}</div>
                  )}
                  {values.targetJobTitle && (
                    <div><Text type="secondary">Target Job Title:</Text> {values.targetJobTitle}</div>
                  )}
                  {!values.keyword && !values.jobTitle && !values.targetJobTitle && (
                    <div><Text type="danger">No search criteria defined!</Text></div>
                  )}
                </div>
                
                {formSectionsData.map((section, index) => (
                  <div key={index} style={modalStyles.formSection} className={cssStyles.modalFormSection}>
                    <div style={modalStyles.sectionTitle} className={cssStyles.modalSectionTitle}>
                      {section.icon}
                      {section.title}
                    </div>
                    <div style={modalStyles.fieldsGrid} className={cssStyles.modalFieldsGrid}>
                      {section.fields.map(fieldName => {
                        const field = advancedFields.find(f => f.name === fieldName);
                        if (!field) return null;
                        const FieldComponent = JobBoardFormFields[field.type];
                        if (!FieldComponent) {
                          console.warn(`Field type "${field.type}" not found for field "${field.name}"`);
                          return null;
                        }
                        return (
                          <div key={field.name} className={cssStyles.modalFormItem}>
                            <FieldComponent
                              name={field.name}
                              label={field.label}
                              {...field.props}
                            />
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
                <div style={{
                  ...modalStyles.footer,
                  borderBottomLeftRadius: '16px',
                  borderBottomRightRadius: '16px'
                }} className={cssStyles.modalFooter}>
                  <Button 
                    onClick={() => updateLocalState({ isAdvanceOptionsOpen: false })}
                    size="large"
                  >
                    Cancel
                  </Button>
                  <Button
                    type="primary"
                    onClick={handleSubmit}
                    icon={<FilterOutlined />}
                    size="large"
                    className={cssStyles.primaryButton}
                    style={{ 
                      background: 'linear-gradient(45deg, #2e7d32, #4caf50)', 
                      borderColor: '#2e7d32' 
                    }}
                  >
                    Apply Filters
                  </Button>
                </div>
              </Form>
            );
          }}
        </Formik>
      </div>
    </div>
  ), [searchParams, handleSubmit, formSectionsData, modalStyles, localState.currentSearchValues]);

  return (
    <NotesProvider>
      <Layout style={useStyles.pageLayout} className={cssStyles.cbSearchContainer}>
        {renderContent()}
        <Modal
          title={null}
          open={localState.isAdvanceOptionsOpen}
          onCancel={() => updateLocalState({ isAdvanceOptionsOpen: false })}
          maskClosable={false}
          keyboard={true}
          destroyOnClose={true}
          footer={null}
          width={1200}
          centered
          className={`${cssStyles.advancedModal} ${cssStyles.cbContainer}`}
          bodyStyle={{ 
            padding: 0, 
            borderRadius: '16px', 
            backgroundColor: 'transparent',
            overflow: 'hidden'
          }}
          style={{ 
            top: 20,
            overflow: 'hidden'
          }}
          maskStyle={{ backgroundColor: 'rgba(0, 0, 0, 0.65)' }}
          wrapClassName={cssStyles.advancedModal}
          modalRender={(node) => (
            <div style={{ 
              overflow: 'hidden', 
              borderRadius: '16px',
              backgroundColor: '#2e7d32'
            }}>
              {node}
            </div>
          )}
        >
          {modalContent}
        </Modal>
      </Layout>
    </NotesProvider>
  );
});

CBSearch.displayName = 'CBSearch';

export default CBSearch;

